import React, { ReactNode, useState } from 'react';
import { ReactComponent as CopyField } from '../../b2b/components/icons/copy.svg';
import { ReactComponent as Help } from '../../b2b/components/icons/help.svg';
import { ReactComponent as ErrorIcon } from '../../b2b/components/icons/warning.svg';
import { ReactComponent as HelpFilled } from '../../common/icons/help-icon-filled.svg';
import Iban from './iban-field';
import './input-field.css';

interface OutlinedInputProps extends React.HTMLAttributes<HTMLInputElement> {
  initialvalue?: string;
  value?: string;
  className?: string;
  icon?: any;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  label?: string;
  extraLabel?: string;
  optional?: boolean;
  hint?: string;
  extraInfo?: ReactNode;
  name?: string;
  type?: string;
  error?: string;
  strict?: boolean;
  readOnly?: boolean;
  autoComplete?: 'off' | 'on';
  withErrorMessage?: boolean;
  disabled?: boolean;
  copyValue?: string;
  additionalText?: string;
  min?: any;
  step?: string;
  placeholder?: string;
  tooltip?: string;
  maxLength?: number;
  tooltipPosition?: 'flex' | 'inline-block';
}

const InputField = React.forwardRef<any, OutlinedInputProps>(
  (
    {
      initialvalue,
      value,
      className,
      icon,
      label,
      extraLabel,
      optional,
      hint,
      name,
      error = false,
      type = 'text',
      onChange,
      onFocus,
      onBlur,
      strict,
      extraInfo,
      autoComplete,
      disabled,
      withErrorMessage = true,
      copyValue = '',
      additionalText = '',
      min,
      step,
      maxLength,
      tooltip,
      // tooltipPosition = 'flex',
      placeholder,
      readOnly,
      ...props
    },
    ref,
  ) => {
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [copied, setcopied] = useState<boolean>(false);
    const [isFocused, setIsFocused] = useState<boolean>();
    const isVisible = !showPassword ? 'password' : 'text';
    const copyToClipboard = () => {
      navigator.clipboard.writeText(copyValue || initialvalue || '');
      setcopied(true);
      setTimeout(() => {
        setcopied(false);
      }, 5000);
    };

    return (
      <div
        className={`input-box ${className || ''} ${error ? 'error' : ' '} ${
          additionalText ? 'input-box-additional-element' : ''
        }`}
      >
        {label && (
          <label
            className={`input-label content-normal ${
              optional ? 'optional' : ''
            }`}
            htmlFor={name}
            style={{ width: `${strict ? 'auto' : ''}` }}
          >
            <span className='input-label-span'>
              <span>
                {label} {extraInfo && <>{extraInfo}</>}{' '}
              </span>
              {extraLabel && <span className='light-grey'>{extraLabel}</span>}
              {hint && (
                <div className='hint-container'>
                  <HelpFilled style={{ marginLeft: '5px', marginTop: '2px' }} />
                  <span className='hint-box content-normal'>{hint}</span>
                </div>
              )}
            </span>

            {type === 'password' && strict && (
              <div className='hover-text pass-hint'>
                <Help style={{ marginLeft: '5px' }} />
                <p className='error sub-bold left tooltip-text' id='bottom'>
                  Passwort mit mindestens 8 Zeichen, eine Zahl, ein
                  Großbuchstabe und ein Sonderzeichen zum Beispiel !,@,$,%,& und
                  *.
                </p>
              </div>
            )}
          </label>
        )}

        <div className='input-inner-container'>
          <div
            className={`${
              icon
                ? 'input-with-icon-container'
                : 'input-without-icon-container'
            }`}
          >
            {icon && (
              <span
                className={`input-icon ${
                  isFocused ? 'input-icon-focused' : ''
                } ${error ? 'input-box-error' : ''} `}
              >
                {icon}
              </span>
            )}
            <div className='input-section'>
              {type === 'iban' ? (
                <Iban
                  defaultValue={initialvalue}
                  id={name}
                  className={`input-text ${error ? 'input-box-error' : ''} ${
                    icon ? 'input-has-icon' : ''
                  } ${isFocused ? 'focused' : ''}`}
                  data-testid={`input-field-${name}`}
                  getInputRef={ref}
                  onFocus={(e) => {
                    if (onFocus) onFocus(e);
                    setIsFocused(true);
                  }}
                  name={name}
                  value={value}
                  disabled={disabled}
                  onChange={onChange}
                  onBlur={(e) => {
                    if (onBlur) onBlur(e);
                    setIsFocused(false);
                  }}
                  placeholder={placeholder}
                />
              ) : (
                <input
                  readOnly={readOnly}
                  defaultValue={initialvalue}
                  id={name}
                  autoComplete={autoComplete}
                  className={`input-text ${error ? 'input-box-error' : ''} ${
                    icon ? 'input-has-icon' : ''
                  } ${isFocused ? 'focused' : ''}`}
                  data-testid={`input-field-${name}`}
                  type={type === 'password' ? isVisible : type}
                  ref={ref}
                  onFocus={(e) => {
                    if (onFocus) onFocus(e);
                    setIsFocused(true);
                  }}
                  name={name}
                  value={value}
                  disabled={disabled}
                  onChange={onChange}
                  onBlur={(e) => {
                    if (onBlur) onBlur(e);
                    setIsFocused(false);
                  }}
                  placeholder={placeholder}
                  onKeyDown={(e) => {
                    if (type === 'number') {
                      if (['e', 'E', '+', '-', '.', ','].includes(e.key)) {
                        e.preventDefault();
                      }
                    }
                  }}
                  onInput={(e) => {
                    const target = e.target as HTMLInputElement;
                    if (type === 'number') {
                      target.value = target.value.replace(/[^0-9]/g, '');
                      if (maxLength && target.value.length > maxLength) {
                        target.value = target.value.slice(0, maxLength);
                      }
                    }
                  }}
                  min={min}
                  step={step}
                  maxLength={maxLength}
                  {...props}
                />
              )}
            </div>
          </div>

          {type === 'password' &&
            (showPassword ? (
              <div
                role='presentation'
                className={`additional-element small-text ${error ? 'withError' : ''} `}
                onClick={() => setShowPassword(!showPassword)}
              >
                Ausblenden
              </div>
            ) : (
              <div
                role='presentation'
                className={`additional-element small-text ${error ? 'withError' : ''} `}
                onClick={() => setShowPassword(!showPassword)}
              >
                Anzeigen
              </div>
            ))}

          {copyValue && (
            <>
              <CopyField
                className={` ${
                  copied
                    ? 'additional-element-copy hide'
                    : 'additional-element-copy'
                } ${error ? 'additional-element-left' : ''} `}
                onClick={copyToClipboard}
              />
              <span
                className={
                  copied
                    ? 'additional-element-copy small-text'
                    : 'additional-element-copy hide'
                }
              >
                Kopiert
              </span>
            </>
          )}

          {additionalText && (
            <span
              className={`additional-element-text small-text ${
                className || ''
              }`}
            >
              {additionalText}
            </span>
          )}

          {error && type !== 'password' && (
            <ErrorIcon className='error-icon-new' />
          )}

          {tooltip && isFocused && !error && (
            <p
              className={`small-text ${error ? 'error' : 'dark-grey'} left`}
              style={{ marginTop: '5px' }}
            >
              {tooltip || ' '}
            </p>
          )}

          {error && withErrorMessage && (
            <p
              className={`small-text ${error ? 'error' : 'dark-grey'} left`}
              style={{ marginTop: '5px' }}
            >
              {error || ' '}
            </p>
          )}
        </div>
      </div>
    );
  },
);

export default InputField;
